import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import FilterBar from '../../components/FilterBar';
import {
    fetchFullReport,
    fetchSettings,
    downloadReportmonthlyperformance,
    changeClassicFilter,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
} from '../../store/modules/reportmonthlyperformance/reportmonthlyperformanceActions';

import KpiCards from './components/KpiCards';
import RevenueCharts from './components/RevenueCharts';
import OrderCharts from './components/OrderCharts';
import ProductionCharts from './components/ProductionCharts';
import CustomerCharts from './components/CustomerCharts';
import InventoryCharts from './components/InventoryCharts';
import PaymentCharts from './components/PaymentCharts';
import MoMComparison from './components/MoMComparison';
import LpoVsWalkinChart from './components/LpoVsWalkinChart';
import VatSummary from './components/VatSummary';
import DiscountAnalysis from './components/DiscountAnalysis';
import PaymentStatusChart from './components/PaymentStatusChart';
import WeeklyTrends from './components/WeeklyTrends';
import OrderStatusMatrix from './components/OrderStatusMatrix';
import CustomerRepeatRate from './components/CustomerRepeatRate';
import ProfitabilityAnalysis from './components/ProfitabilityAnalysis';
import CancellationsChart from './components/CancellationsChart';
import SupplierAnalysis from './components/SupplierAnalysis';
import BrandAnalysis from './components/BrandAnalysis';
import CustomerDeepDive from './components/CustomerDeepDive';
import ItemCategoryMix from './components/ItemCategoryMix';
import BranchComparison from './components/BranchComparison';
import DailyAverages from './components/DailyAverages';
import TopSellingItems from './components/TopSellingItems';
import StockAdjustments from './components/StockAdjustments';
import CashMemoDirectDetails from './components/CashMemoDirectDetails';
import EnrichedVat from './components/EnrichedVat';
import RecurringCustomers from './components/RecurringCustomers';
import ItemTypeAnalysis from './components/ItemTypeAnalysis';

const Reportmonthlyperformance = () => {
    const dispatch = useDispatch();
    const state = useSelector((store) => store.reportmonthlyperformance);

    useEffect(() => {
        const load = async () => {
            await dispatch(fetchSettings());
            await dispatch(fetchFullReport());
        };
        load();
    }, [dispatch]);

    const handleDownload = async () => {
        try {
            const response = await dispatch(downloadReportmonthlyperformance('pdf'));
            if (response?.url) {
                window.open(response.url, '_blank');
            }
        } catch (error) {
            toast.error(error.message || 'Error downloading report', {
                position: 'top-right',
                autoClose: 3000,
            });
        }
    };

    const handlePrint = useCallback(() => {
        window.print();
    }, []);

    const handleFilterBarUpdate = async ({ action }) => {
        if (action === 'fetchList' || action === 'fetchListClassicFilters') {
            await dispatch(fetchFullReport());
        }
    };

    const monthLabel = state.month
        ? new Date(`${state.month}-01T00:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
        : '';

    return (
        <div className="page-content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                            <div>
                                <h4 className="mb-sm-0">Unified Executive Monthly Report</h4>
                                {monthLabel ? <span className="text-muted ms-2">{monthLabel}</span> : null}
                            </div>
                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={handlePrint}
                                    title="Print Report"
                                >
                                    <i className="fas fa-print me-1"></i> Print
                                </button>
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={handleDownload}
                                    disabled={state.downloading}
                                    title="Download PDF"
                                >
                                    {state.downloading ? (
                                        <span className="spinner-border spinner-border-sm me-1" role="status" />
                                    ) : (
                                        <i className="fas fa-file-pdf me-1" />
                                    )}
                                    Export PDF
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                <FilterBar
                                    title="Unified Executive Monthly Report"
                                    name="reportmonthlyperformance"
                                    identifier="reportmonthlyperformance"
                                    module={state}
                                    onInputSearch={() => {}}
                                    onDownloadClick={handleDownload}
                                    onCreateButtonClick={() => {}}
                                    availableActions={['export_pdf']}
                                    onFilterBarUpdate={handleFilterBarUpdate}
                                    setDefaultFilter={setDefaultFilter}
                                    changeCondition={changeCondition}
                                    addNewCondition={addNewCondition}
                                    updateOptionRule={updateOptionRule}
                                    updateOptionValue={updateOptionValue}
                                    removeCondition={removeCondition}
                                    saveFilterCondition={saveFilterCondition}
                                    onClassicFilterChange={(name, value) => dispatch(changeClassicFilter(name, value))}
                                />

                                {state.loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="text-muted mt-2">Loading executive report data...</p>
                                    </div>
                                ) : (
                                    <div className="executive-report">
                                        {/* Executive KPI Cards */}
                                        <KpiCards cards={state.kpiCards} />

                                        {/* Revenue & Financial Charts */}
                                        <RevenueCharts
                                            revenueTrends={state.revenueTrends}
                                            revenueByLocation={state.revenueByLocation}
                                            revenueByPaymentMethod={state.revenueByPaymentMethod}
                                        />

                                        {/* Order Pipeline & Delivery */}
                                        <OrderCharts
                                            orderPipeline={state.orderPipeline}
                                            orderTypeBreakdown={state.orderTypeBreakdown}
                                            deliveryPerformance={state.deliveryPerformance}
                                        />

                                        {/* Production & Tailor Productivity */}
                                        <ProductionCharts
                                            topTailors={state.topTailors}
                                            dailyProduction={state.dailyProduction}
                                        />

                                        {/* Customer Analysis */}
                                        <CustomerCharts
                                            topCustomers={state.topCustomers}
                                            customerTypeBreakdown={state.customerTypeBreakdown}
                                            newCustomersTrend={state.newCustomersTrend}
                                        />

                                        {/* Inventory & Stock */}
                                        <InventoryCharts
                                            stockUsage={state.stockUsage}
                                            lowStockAlerts={state.lowStockAlerts}
                                            purchaseSummary={state.purchaseSummary}
                                        />

                                        {/* Payments & Collections */}
                                        <PaymentCharts
                                            paymentCollection={state.paymentCollection}
                                            outstandingReceivables={state.outstandingReceivables}
                                        />

                                        {/* MoM Comparison */}
                                        <MoMComparison data={state.momComparison} />

                                        {/* Enriched Analysis */}
                                        {(state.lpoVsWalkinRevenue || state.vatSummary || state.discountAnalysis) && (
                                            <div className="mb-4">
                                                <h5 className="mb-3 border-bottom pb-2">Enriched Analysis</h5>
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <LpoVsWalkinChart data={state.lpoVsWalkinRevenue} />
                                                    </div>
                                                    <div className="col-md-3">
                                                        <VatSummary data={state.vatSummary} />
                                                    </div>
                                                    <div className="col-md-3">
                                                        <DiscountAnalysis data={state.discountAnalysis} />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {(state.paymentStatusBreakdown || state.customerRepeatRate) && (
                                            <div className="mb-4">
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <PaymentStatusChart data={state.paymentStatusBreakdown} />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <CustomerRepeatRate data={state.customerRepeatRate} />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {(state.weeklyTrends || state.orderStatusByLocation) && (
                                            <div className="mb-4">
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <WeeklyTrends data={state.weeklyTrends} />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <OrderStatusMatrix data={state.orderStatusByLocation} />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Phase 2: Profitability & Cost Analysis */}
                                        {state.profitabilityAnalysis && (
                                            <div className="mb-4">
                                                <h5 className="mb-3 border-bottom pb-2">Profitability & Cost Analysis</h5>
                                                <div className="row g-3">
                                                    <div className="col-12">
                                                        <ProfitabilityAnalysis data={state.profitabilityAnalysis} />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Phase 2: Supplier + Cancellations */}
                                        {(state.supplierAnalysis || state.cancellationsAnalysis || state.fulfillmentTime) && (
                                            <div className="mb-4">
                                                <h5 className="mb-3 border-bottom pb-2">Supplier & Operations</h5>
                                                <div className="row g-3">
                                                    {state.supplierAnalysis && (
                                                        <div className="col-md-6">
                                                            <SupplierAnalysis data={state.supplierAnalysis} />
                                                        </div>
                                                    )}
                                                    {state.cancellationsAnalysis && (
                                                        <div className="col-md-6">
                                                            <CancellationsChart data={state.cancellationsAnalysis} />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Phase 2: Brand + Customer Deep Dive */}
                                        {(state.brandAnalysis || state.customerDeepDive) && (
                                            <div className="mb-4">
                                                <h5 className="mb-3 border-bottom pb-2">Brand & Customer Deep Dive</h5>
                                                <div className="row g-3">
                                                    {state.brandAnalysis && (
                                                        <div className="col-md-6">
                                                            <BrandAnalysis data={state.brandAnalysis} />
                                                        </div>
                                                    )}
                                                    {state.customerDeepDive && (
                                                        <div className="col-md-6">
                                                            <CustomerDeepDive data={state.customerDeepDive} />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Gap-filler: Existing backend data wired to frontend */}
                                        <div className="mb-4">
                                            <h5 className="mb-3 border-bottom pb-2">Additional Metrics</h5>
                                            <div className="row g-3">
                                                {state.dailyAverages && (
                                                    <div className="col-md-4">
                                                        <DailyAverages data={state.dailyAverages} />
                                                    </div>
                                                )}
                                                {state.itemCategoryMix && state.itemCategoryMix.length > 0 && (
                                                    <div className="col-md-4">
                                                        <ItemCategoryMix data={state.itemCategoryMix} />
                                                    </div>
                                                )}
                                                {state.branchComparison && state.branchComparison.length > 0 && (
                                                    <div className="col-md-4">
                                                        <BranchComparison data={state.branchComparison} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Phase 3: New Data Enrichments */}
                                        <div className="mb-4">
                                            <h5 className="mb-3 border-bottom pb-2">Additional Data Insights</h5>
                                            <div className="row g-3">
                                                {(state.topSellingItems?.by_quantity?.length > 0 || state.topSellingItems?.by_revenue?.length > 0) && (
                                                    <div className="col-md-6">
                                                        <TopSellingItems data={state.topSellingItems} />
                                                    </div>
                                                )}
                                                {state.stockAdjustments?.summary && (
                                                    <div className="col-md-6">
                                                        <StockAdjustments data={state.stockAdjustments} />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="row g-3 mt-1">
                                                {state.cashMemoDirectDetails?.summary && (
                                                    <div className="col-md-6">
                                                        <CashMemoDirectDetails data={state.cashMemoDirectDetails} />
                                                    </div>
                                                )}
                                                {state.enrichedVat && (
                                                    <div className="col-md-4">
                                                        <EnrichedVat data={state.enrichedVat} />
                                                    </div>
                                                )}
                                                {state.recurringCustomers && (
                                                    <div className="col-md-4">
                                                        <RecurringCustomers data={state.recurringCustomers} />
                                                    </div>
                                                )}
                                                {state.itemTypeAnalysis && (
                                                    <div className="col-md-4">
                                                        <ItemTypeAnalysis data={state.itemTypeAnalysis} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Reportmonthlyperformance;
